import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AppNav, { NAV_SAFE_CLASS } from "@/components/nav/AppNav";
import { useCall } from "@/context/CallContext";
import { Phone, PhoneMissed, PhoneIncoming, PhoneOutgoing, Video, ArrowLeft, Loader2 } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const same = d.toDateString() === now.toDateString();
    if (same) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString([], { day: "2-digit", month: "short" }) + " · " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function fmtDuration(secs) {
  if (!secs || secs <= 0) return "—";
  const s = Math.floor(secs);
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return mm > 0 ? `${mm}dk ${ss}sn` : `${ss}sn`;
}

function CallRow({ call, onCall }) {
  const peer = call.peer || {};
  const isMissed = call.status === "missed" && call.direction === "incoming";
  const isVideo = call.call_type === "video";
  const isIncoming = call.direction === "incoming";
  const IconStatus = isMissed ? PhoneMissed : (isIncoming ? PhoneIncoming : PhoneOutgoing);
  const iconColor = isMissed ? "#FF3B30" : (call.status === "answered" ? "#0A0A0A" : "#555");
  return (
    <div className="flex items-center gap-3 p-3 border-hard-2 rounded-2xl bg-white shadow-hard-sm" data-testid={`call-row-${call.id}`}>
      <div className="w-11 h-11 shrink-0 rounded-full border-hard-2 bg-[#CCFF00] flex items-center justify-center overflow-hidden font-display">
        {peer.picture
          ? <img src={peer.picture} alt="" className="w-full h-full object-cover" />
          : <span>{(peer.name || "?")[0].toUpperCase()}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-black text-sm truncate">{peer.name || "(kullanıcı)"}</div>
        <div className="flex items-center gap-1.5 text-[11px] text-[#555] mt-0.5">
          <IconStatus strokeWidth={3} className="w-3 h-3" style={{ color: iconColor }} />
          {isVideo && <Video strokeWidth={3} className="w-3 h-3" />}
          <span>{fmtDate(call.started_at)}</span>
          {call.duration_secs != null && call.status === "answered" && (
            <span className="text-[#0A0A0A]">· {fmtDuration(call.duration_secs)}</span>
          )}
          {isMissed && <span className="text-[#FF3B30] font-black uppercase tracking-widest">· CEVAPSIZ</span>}
        </div>
      </div>
      <button
        onClick={() => onCall(peer, isVideo ? "video" : "audio")}
        data-testid={`call-row-callback-${call.id}`}
        className="w-9 h-9 border-hard-2 rounded-lg bg-[#CCFF00] flex items-center justify-center press-btn shadow-hard-sm shrink-0"
        aria-label="Tekrar ara"
      >
        {isVideo ? <Video strokeWidth={3} className="w-4 h-4" /> : <Phone strokeWidth={3} className="w-4 h-4" />}
      </button>
    </div>
  );
}

export default function CallHistoryPage() {
  const navigate = useNavigate();
  const { startCall } = useCall();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/calls/history");
        setCalls(data);
      } catch (e) { setError(formatApiError(e)); }
      setLoading(false);
    })();
  }, []);

  const handleCallback = (peer, type) => {
    startCall({ toUser: peer, type });
  };

  return (
    <div className={`min-h-[100dvh] bg-[#F9F8F6] text-[#0A0A0A] ${NAV_SAFE_CLASS}`} data-testid="call-history-page">
      <AppNav />
      <div className="max-w-2xl mx-auto p-4 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            data-testid="call-history-back"
            className="w-10 h-10 border-hard-2 rounded-xl bg-white flex items-center justify-center press-btn shadow-hard-sm"
            aria-label="Geri"
          >
            <ArrowLeft strokeWidth={3} className="w-4 h-4" />
          </button>
          <div>
            <div className="text-[10px] uppercase font-black tracking-widest text-[#555]">Arama Geçmişi</div>
            <h1 className="font-display text-3xl md:text-4xl leading-none">Aramalar</h1>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>
        )}

        {!loading && error && (
          <div className="border-hard-2 bg-[#FFD0C2] rounded-xl px-3 py-2 text-sm font-bold" data-testid="call-history-error">{error}</div>
        )}

        {!loading && !error && calls.length === 0 && (
          <div className="text-center py-16" data-testid="call-history-empty">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-[#CCFF00] border-hard shadow-hard flex items-center justify-center mb-3">
              <Phone strokeWidth={3} className="w-6 h-6" />
            </div>
            <div className="font-display text-xl">Henüz arama yok</div>
            <p className="text-sm text-[#555] mt-1">Bir sohbet aç ve sesli ya da görüntülü ara.</p>
          </div>
        )}

        <div className="space-y-2">
          {calls.map((c) => <CallRow key={c.id} call={c} onCall={handleCallback} />)}
        </div>
      </div>
    </div>
  );
}
